import React from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const Licenses = () => {
  const tableOfContents = [
    { id: 'company-info', title: 'Company Information' },
    { id: 'state-licenses', title: 'State Insurance Licenses' },
    { id: 'compliance', title: 'Regulatory Compliance' },
    { id: 'updates', title: 'License Updates' }
  ];

  // This data should be easily editable - see README.md
  const stateLicenses = [
    { state: 'Alabama', licenseNumber: '[License #]', status: 'Active' },
    { state: 'Alaska', licenseNumber: '[License #]', status: 'Active' },
    { state: 'Arizona', licenseNumber: '[License #]', status: 'Active' },
    { state: 'Arkansas', licenseNumber: '[License #]', status: 'Active' },
    { state: 'California', licenseNumber: '[License #]', status: 'Active' },
    // Add remaining states - see README for maintenance instructions
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <section className="py-20">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Table of Contents - Sticky on desktop */}
            <div className="lg:col-span-1">
              <div className="lg:sticky lg:top-24">
                <h2 className="text-lg font-semibold text-foreground mb-4">Table of Contents</h2>
                <nav className="space-y-2">
                  {tableOfContents.map((item) => (
                    <a
                      key={item.id}
                      href={`#${item.id}`}
                      className="block text-sm text-muted-foreground hover:text-primary transition-colors"
                    >
                      {item.title}
                    </a>
                  ))}
                </nav>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3">
              <h1 className="text-4xl font-bold text-foreground mb-4">Insurance Licenses</h1>
              <p className="text-muted-foreground mb-8">Last updated: January 2024</p>
              
              <div className="prose prose-lg max-w-none space-y-8">
                <section id="company-info">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">Company Information</h2>
                  <div className="text-muted-foreground space-y-4">
                    <div className="bg-muted p-6 rounded-lg">
                      <p><strong className="text-foreground">Legal Name:</strong> Safurra Insurance Services, LLC</p>
                      <p><strong className="text-foreground">Doing Business As (DBA):</strong> Safurra</p>
                      <p><strong className="text-foreground">Headquarters:</strong> Orange County, California</p>
                      <p><strong className="text-foreground">National Producer Number (NPN):</strong> [NPN Number - See README.md to update]</p>
                      <p><strong className="text-foreground">Business Type:</strong> Licensed Insurance Agency</p>
                    </div>
                    <p>
                      Safurra Insurance Services, LLC is a licensed insurance agency operating across the United States. 
                      We are authorized to sell, solicit, and negotiate insurance products on behalf of licensed insurance carriers.
                    </p>
                  </div>
                </section>

                <section id="state-licenses">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">State Insurance Licenses</h2>
                  <div className="text-muted-foreground space-y-4">
                    <p>
                      Safurra holds active insurance licenses in all 50 states and the District of Columbia. 
                      Below is a current list of our state license numbers:
                    </p>
                    
                    <div className="bg-muted p-6 rounded-lg mb-6">
                      <p className="text-sm text-muted-foreground">
                        <strong className="text-foreground">Note for administrators:</strong> License numbers should be updated in the README.md file. 
                        This table is generated from data that can be easily maintained.
                      </p>
                    </div>

                    <div className="overflow-x-auto">
                      <table className="w-full border border-border rounded-lg">
                        <thead>
                          <tr className="bg-muted">
                            <th className="px-4 py-3 text-left text-foreground font-semibold border-b border-border">State</th>
                            <th className="px-4 py-3 text-left text-foreground font-semibold border-b border-border">License Number</th>
                            <th className="px-4 py-3 text-left text-foreground font-semibold border-b border-border">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {stateLicenses.slice(0, 5).map((license, index) => (
                            <tr key={license.state} className={index % 2 === 0 ? 'bg-background' : 'bg-muted/50'}>
                              <td className="px-4 py-3 border-b border-border">{license.state}</td>
                              <td className="px-4 py-3 border-b border-border">{license.licenseNumber}</td>
                              <td className="px-4 py-3 border-b border-border">
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                                  {license.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                          <tr>
                            <td colSpan={3} className="px-4 py-3 text-center text-muted-foreground border-b border-border">
                              <em>Additional 45 states and DC - Complete list available upon request</em>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </section>

                <section id="compliance">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">Regulatory Compliance</h2>
                  <div className="text-muted-foreground space-y-4">
                    <p>
                      Safurra maintains all required licenses and adheres to state insurance regulations and compliance requirements, including:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Continuing education requirements for licensed agents</li>
                      <li>State-specific disclosure and advertising requirements</li>
                      <li>Consumer protection and privacy regulations</li>
                      <li>Financial responsibility and bonding requirements</li>
                      <li>Regular license renewals and regulatory filings</li>
                    </ul>
                    <p>
                      All our licensed agents maintain current certifications and complete required continuing education 
                      to ensure they stay up-to-date with insurance laws and best practices.
                    </p>
                  </div>
                </section>

                <section id="updates">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">License Updates</h2>
                  <div className="text-muted-foreground space-y-4">
                    <p>
                      License information is updated regularly to reflect current status. If you need to verify 
                      a specific license or have questions about our licensing, please contact us:
                    </p>
                    <div className="bg-muted p-6 rounded-lg">
                      <p><strong className="text-foreground">Licensing Inquiries:</strong> <a href="mailto:support@safurra.com" className="text-primary hover:underline">support@safurra.com</a></p>
                      <p><strong className="text-foreground">Regulatory Contact:</strong> Available upon request</p>
                    </div>
                    <p className="text-sm">
                      License numbers can be verified through each state's Department of Insurance website. 
                      Links to state regulators are available upon request.
                    </p>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default Licenses;